import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { logDataModels } from '../dataModelsLogger';
import type { GovernanceLevel } from '../components/governanceStore';

export type PolicyEditorMode = 'create' | 'edit' | 'view';

export interface PolicyEditorView {
  mode: PolicyEditorMode;
  level: GovernanceLevel;
  policyId?: string;
  spaceId?: string;
  modelId?: string;
  tableId?: string;
}

interface PolicyEditorNavValue {
  view: PolicyEditorView | null;
  open: (view: PolicyEditorView) => void;
  close: () => void;
}

const PolicyEditorNavContext = createContext<PolicyEditorNavValue | null>(null);

function pageUrl(hash = ''): string {
  return `${window.location.pathname}${window.location.search}${hash}`;
}

export function buildPolicyHash(view: PolicyEditorView): string {
  const parts = ['policy', view.mode];
  if (view.level === 'asset' && view.spaceId && view.modelId && view.tableId) {
    parts.push('table', view.spaceId, view.modelId, view.tableId);
  } else if (view.level === 'model' && view.spaceId && view.modelId) {
    parts.push('model', view.spaceId, view.modelId);
  } else if (view.spaceId) {
    parts.push('workspace', view.spaceId);
  }
  if (view.policyId) parts.push(view.policyId);
  return `#${parts.join('/')}`;
}

export function parsePolicyHash(hash: string): PolicyEditorView | null {
  const raw = hash.replace(/^#/, '').replace(/^\/+/, '');
  if (!raw.startsWith('policy/')) return null;
  const parts = raw.split('/').filter(Boolean);
  const mode = parts[1] as PolicyEditorMode | undefined;
  if (mode !== 'create' && mode !== 'edit' && mode !== 'view') return null;

  let index = 2;
  let level: GovernanceLevel = 'vault';
  let spaceId: string | undefined;
  let modelId: string | undefined;
  let tableId: string | undefined;
  const scope = parts[index];
  if (scope === 'workspace' && parts[index + 1]) {
    spaceId = parts[index + 1];
    index += 2;
    level = 'vault';
  } else if (scope === 'model' && parts[index + 1] && parts[index + 2]) {
    spaceId = parts[index + 1];
    modelId = parts[index + 2];
    index += 3;
    level = 'model';
  } else if (scope === 'table' && parts[index + 1] && parts[index + 2] && parts[index + 3]) {
    spaceId = parts[index + 1];
    modelId = parts[index + 2];
    tableId = parts[index + 3];
    index += 4;
    level = 'asset';
  }

  const policyId = parts.slice(index).join('/') || undefined;
  if ((mode === 'edit' || mode === 'view') && !policyId) return null;
  return { mode, level, spaceId, modelId, tableId, policyId };
}

function applyHash(view: PolicyEditorView | null, replace: boolean) {
  const next = view ? buildPolicyHash(view) : '';
  if (window.location.hash === next) return;
  const url = pageUrl(next);
  if (replace) window.history.replaceState(null, '', url);
  else window.history.pushState(null, '', url);
}

export function PolicyEditorNavProvider({ children }: { children: React.ReactNode }) {
  const [view, setView] = useState<PolicyEditorView | null>(() => (
    typeof window === 'undefined' ? null : parsePolicyHash(window.location.hash)
  ));

  useEffect(() => {
    const sync = () => {
      const raw = window.location.hash.replace(/^#/, '');
      const next = parsePolicyHash(window.location.hash);
      setView(next);
      if (next) {
        logDataModels('app', 'governance_policy_hash_opened', { mode: next.mode, level: next.level, policyId: next.policyId ?? '' });
        return;
      }
      if (raw.startsWith('policy/')) logDataModels('warning', 'governance_policy_hash_invalid', { hash: window.location.hash });
    };
    window.addEventListener('hashchange', sync);
    window.addEventListener('popstate', sync);
    return () => {
      window.removeEventListener('hashchange', sync);
      window.removeEventListener('popstate', sync);
    };
  }, []);

  const value = useMemo<PolicyEditorNavValue>(
    () => ({
      view,
      open: (next) => {
        setView(next);
        applyHash(next, false);
        logDataModels('app', 'governance_policy_route_opened', { mode: next.mode, level: next.level, policyId: next.policyId ?? '' });
      },
      close: () => {
        setView(null);
        applyHash(null, true);
      },
    }),
    [view],
  );
  return <PolicyEditorNavContext.Provider value={value}>{children}</PolicyEditorNavContext.Provider>;
}

export function usePolicyEditorNav(): PolicyEditorNavValue {
  const value = useContext(PolicyEditorNavContext);
  if (!value) throw new Error('Policy editor navigation is only available inside Data models.');
  return value;
}
